/**
 * Permission Context Utility
 * Handles different permission responses based on timezone and data_type combinations
 */

// Define the 4 possible API request combinations
export const PERMISSION_CONTEXTS = {
  UTC_NONSKAN: { timezone: 'UTC', data_type: 'nonskan' },
  UTC_SKAN: { timezone: 'UTC', data_type: 'skan' },
  JST_NONSKAN: { timezone: 'JST', data_type: 'nonskan' },
  JST_SKAN: { timezone: 'JST', data_type: 'skan' }
};

/**
 * Get the current permission context based on timezone and data_type
 * @param {string} timezone - 'UTC' or 'JST'
 * @param {string} dataType - 'skan' or 'nonskan'
 * @returns {string} - Context key
 */
export const getPermissionContext = (timezone, dataType) => {
  const contextKey = `${timezone}_${dataType.toUpperCase()}`;
  return contextKey;
};

/**
 * Check if the current context matches the stored permissions context
 * @param {Object} userPermissions - Current user permissions from Redux
 * @param {string} currentTimezone - Current timezone
 * @param {string} currentDataType - Current data type
 * @returns {boolean} - True if context matches
 */
export const isContextMatch = (userPermissions, currentTimezone, currentDataType) => {
  if (!userPermissions || !userPermissions.context) {
    return false;
  }

  return userPermissions.context.timezone === currentTimezone && userPermissions.context.data_type === currentDataType;
};

/**
 * Get effective permissions based on current context
 * @param {Object} userPermissions - Current user permissions from Redux
 * @param {string} currentTimezone - Current timezone
 * @param {string} currentDataType - Current data type
 * @returns {Object} - Effective permissions for current context
 */
export const getEffectivePermissions = (userPermissions, currentTimezone, currentDataType) => {
  // If no permissions or context doesn't match, return empty permissions
  if (!userPermissions || !isContextMatch(userPermissions, currentTimezone, currentDataType)) {
    return {
      tabs: [],
      widgets: [],
      context: {
        timezone: currentTimezone,
        data_type: currentDataType
      }
    };
  }

  return userPermissions;
};

/**
 * Check if a widget is allowed based on current context
 * @param {string} widgetName - Widget name to check
 * @param {Object} userPermissions - Current user permissions
 * @param {string} currentTimezone - Current timezone
 * @param {string} currentDataType - Current data type
 * @returns {boolean} - True if widget is allowed
 */
export const isWidgetAllowedForContext = (widgetName, userPermissions, currentTimezone, currentDataType) => {
  const effectivePermissions = getEffectivePermissions(userPermissions, currentTimezone, currentDataType);

  // If no permissions object or no widgets array, show all widgets
  if (!effectivePermissions || !effectivePermissions.widgets) {
    return true;
  }

  // If widgets array is empty (explicitly set to show all), show all widgets
  if (effectivePermissions.widgets.length === 0) {
    return true;
  }

  // Handle widget name mapping for different contexts
  const widgetMappings = {
    installs: ['installs', 'mmp_installs'], // 'installs' can be mapped to either 'installs' or 'mmp_installs'
    mmp_installs: ['installs', 'mmp_installs'], // 'mmp_installs' can be mapped to either 'installs' or 'mmp_installs'
    Impressions: ['Impressions', 'impressions'], // Handle case variations
    clicks: ['clicks', 'Clicks'], // Handle case variations
    media_cost: ['media_cost', 'Media Cost'], // Handle different naming conventions
    kpi_events: ['kpi_events', 'KPI Events'], // Handle case variations
    mmp_events: ['mmp_events', 'MMP Events'], // Handle case variations
    skan_installs: ['skan_installs', 'SKAN Installs'], // Handle case variations
    skan_events: ['skan_events', 'SKAN Events'] // Handle case variations
  };

  // Check if the widget name is directly in the permissions
  if (effectivePermissions.widgets.includes(widgetName)) {
    return true;
  }

  // Check if any of the mapped widget names are in the permissions
  const mappedNames = widgetMappings[widgetName] || [widgetName];
  return mappedNames.some((mappedName) => effectivePermissions.widgets.includes(mappedName));
};

/**
 * Check if a tab is allowed based on current context
 * @param {string} tabName - Tab name to check
 * @param {Object} userPermissions - Current user permissions
 * @param {string} currentTimezone - Current timezone
 * @param {string} currentDataType - Current data type
 * @returns {boolean} - True if tab is allowed
 */
export const isTabAllowedForContext = (tabName, userPermissions, currentTimezone, currentDataType) => {
  const effectivePermissions = getEffectivePermissions(userPermissions, currentTimezone, currentDataType);

  // If no permissions object or no tabs array, show all tabs
  if (!effectivePermissions || !effectivePermissions.tabs) {
    return true;
  }

  // If tabs array is empty (explicitly set to show all), show all tabs
  if (effectivePermissions.tabs.length === 0) {
    return true;
  }

  return effectivePermissions.tabs.includes(tabName);
};

/**
 * Get context-specific widget mappings
 * Different contexts might have different widget names or behaviors
 * @param {string} timezone - Current timezone
 * @param {string} dataType - Current data type
 * @returns {Object} - Widget mappings for the context
 */
export const getContextWidgetMappings = (timezone, dataType) => {
  const context = getPermissionContext(timezone, dataType);

  switch (context) {
    case 'UTC_NONSKAN':
      return {
        // Android/Non-SKAN specific mappings
        installs: 'Installs',
        impressions: 'Impressions',
        clicks: 'Clicks',
        media_cost: 'Media Cost',
        kpi_events: 'KPI Events'
      };
    case 'UTC_SKAN':
      return {
        // iOS/SKAN specific mappings
        installs: 'MMP Installs',
        skan_installs: 'SKAN Installs',
        impressions: 'Impressions',
        clicks: 'Clicks',
        media_cost: 'Media Cost',
        mmp_events: 'MMP Events',
        skan_events: 'SKAN Events'
      };
    case 'JST_NONSKAN':
      return {
        // JST Non-SKAN specific mappings
        installs: 'Installs',
        impressions: 'Impressions',
        clicks: 'Clicks',
        media_cost: 'Media Cost',
        kpi_events: 'KPI Events'
      };
    case 'JST_SKAN':
      return {
        // JST SKAN specific mappings
        installs: 'MMP Installs',
        skan_installs: 'SKAN Installs',
        impressions: 'Impressions',
        clicks: 'Clicks',
        media_cost: 'Media Cost',
        mmp_events: 'MMP Events',
        skan_events: 'SKAN Events'
      };
    default:
      return {};
  }
};
